import React from 'react';
import { Popover, Typography } from '@mui/material';
import { fontSize, palette } from '../../../';
import { Checkbox } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import {
  deleteDashBoardSelectedTabTiles,
  updateDashBoardSelectedTabTiles,
} from '../../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';
import { groupProp } from '../../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import { AlertColor } from '@mui/material/Alert';
import {
  setSyncFilterGroup,
  setLastModifiedFilter,
  setOptionsAndSelections,
  setFiltersOfGroup,
} from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import FetchData, { IAPIResponse } from '../../ServerCall/FetchData';
import { IChartFIlter } from '../interfaces';
import Logger from '../../../Logger';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import { updateDashboardAnyConditionMatch } from '../../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';

interface Props {
  group: groupProp;
  setSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setOpenAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setTestMessage: React.Dispatch<React.SetStateAction<string>>;
  showPopover: boolean;
  selectedGroupTabTilesList: any;
  setAnchorElSyncFilter: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
  dashboardTabTileList: any;
  anchorEl: HTMLButtonElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
  setShowPopover: React.Dispatch<React.SetStateAction<boolean>>;
  link: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * rendered by ChartFilterGroups.tsx
 * @returns a popover for dashboard while clicking more icon in GoupHeader which screen is dashboard. It contains a checkbox to enable or disable sync filter functionality and list of dashboard tiles(tiles which are selected in dashboard).
 */
const DashboardFilterGroupPopover = ({
  group,
  setSeverity,
  setOpenAlert,
  setTestMessage,
  showPopover,
  selectedGroupTabTilesList,
  setAnchorElSyncFilter,
  dashboardTabTileList,
  anchorEl,
  setAnchorEl,
  setShowPopover,
  link,
  setLoading,
}: Props) => {
  const { tabTileProps, token, groups, anyConditionMatch } = useSelector((state: RootState) => ({
    tabTileProps: state.tabTileProps,
    token: state.isLogged.accessToken,
    groups: state.chartFilterGroup.groups,
    anyConditionMatch: state.dashBoardFilterGroup.any_condition_match ?? true,
  }));
  const dispatch = useDispatch();

  const dsDetails = tabTileProps.selectedDataSetList.find(
    (dataset: any) => dataset.id === group.dsIdForGroup,
  );
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleCBChange = (event: any) => {
    if (event.target.checked) {
      dispatch(updateDashBoardSelectedTabTiles(group.id, event.target.id));
    } else {
      dispatch(
        deleteDashBoardSelectedTabTiles(
          group.id,
          selectedGroupTabTilesList.findIndex((id: string) => id === event.target.id),
        ),
      );
    }
    dispatch(callQueryAPI(event.target.id, true));
    dispatch(callQueryAPIDashboard(true));
  };

  function modifyOptions(syncEnabled: boolean) {
    Logger('info', `Sync filter enabled:${syncEnabled}`);
    if (!syncEnabled) {
      const filters = group.filters;
      if (!filters || filters.length < 1) return;
      const pickListIndices: number[] = [];
      const promises: Promise<IAPIResponse<Record<string, any>>>[] = [];
      filters.forEach((filter: IChartFIlter, idx: number) => {
        if (filter.fieldtypeoption === 'Pick List') {
          pickListIndices.push(idx);
          const payload = {
            tableId: filter.tableId,
            fieldName: filter.displayname,
            dataType: filter.dataType,
            filterOption: 'allValues',
            ...(filter.dataType === 'timestamp' || filter.dataType === 'date'
              ? { timeGrain: filter.prefix ?? 'year' }
              : {}),
          };

          let url = '';

          if (link && link !== '') {
            url = `open-link/filter-options?datasetid=${
              dsDetails.id
            }&workspaceId=${dsDetails.workSpaceId}&${
              dsDetails.isFlatFileData ? '' : `dbconnectionid=${dsDetails.connectionId}`
            }&linkId=${link}`;
          } else {
            url = `filter-options?datasetid=${dsDetails.id}&workspaceId=${dsDetails.workSpaceId}&${
              dsDetails.isFlatFileData ? '' : `dbconnectionid=${dsDetails.connectionId}`
            }`;
          }

          promises.push(
            FetchData({
              checkToken: link === '',
              method: 'POST',
              url: url,
              requestType: 'withData',
              headers:
                link && link !== ''
                  ? {
                      'Content-Type': 'application/json',
                      'X-TENANT-ID': 'community',
                    }
                  : {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
              data: payload,
            }),
          );
        }
      });
      if (promises.length > 0) {
        (async () => {
          try {
            setLoading(true);
            const res = await Promise.all(promises);
            res.forEach((response, idx: number) => {
              if (response.status && response.data) {
                const avalableOptions = response.data[Object.keys(response.data)[0]];
                const result = avalableOptions.map((value: any) => {
                  return value !== null
                    ? typeof value === 'boolean'
                      ? value
                        ? 'True'
                        : 'False' // Convert boolean to string
                      : value
                    : '(Blank)';
                });
                const options = ['(All)', ...result];
                const filterIndex = pickListIndices[idx];
                filters[filterIndex].userSelection = options;
                filters[filterIndex].rawselectmembers = options;
              }
            });
            dispatch(setFiltersOfGroup(group.id, filters));
          } catch (e) {
            setLoading(false);
          } finally {
            setLoading(false);
          }
        })();
      }
    } else {
      const filters = group.filters;
      if (!filters || filters.length === 0) return;
      filters.forEach((filter: IChartFIlter, idx: number) => {
        if (filter.fieldtypeoption === 'Pick List') {
          const options = filter.rawselectmembersOriginal;
          dispatch(
            setOptionsAndSelections(group.id, options, filter.userSelection ?? options, filter.uId),
          );
          dispatch(setLastModifiedFilter(group.id, filter.uId));
        }
      });
    }
  }

  const handleClose = () => {
    setShowPopover(false);
    setAnchorEl(null);
  };

  return (
    <Popover
      open={showPopover}
      id={id}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <div
        className="datasetListPopover"
        style={{
          paddingTop: 0,
          paddingLeft: '0.5rem',
        }}
      >
        <div
          className="menu-option"
          style={{
            paddingLeft: '0.5rem',
            paddingTop: '0.5rem',
          }}
        >
          <h3
            className="text-primary"
            style={{
              margin: '0.3rem 0 0.3rem 0',
              fontSize: fontSize.medium,
            }}
          >
            Sync filter
          </h3>
          <div
            className="option-content"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transform: 'translateX(-4px)',
            }}
          >
            <Checkbox
              checked={group.syncFilter}
              onClick={(e) => {
                if (
                  group.syncFilter === false &&
                  groups[group.id].filters?.some((calc: any) => calc.isCalculatedField === true)
                ) {
                  setSeverity('warning');
                  setOpenAlert(true);
                  setTestMessage(
                    `${
                      groups[group.id].name
                    } has a calculated field.\nSync filter is not available for calculated field`,
                  );
                } else {
                  setAnchorElSyncFilter(null);
                  modifyOptions(!group.syncFilter);
                  dispatch(setSyncFilterGroup(group.id, !group.syncFilter));
                }
              }}
              sx={{
                padding: 0,
                transform: 'scale(0.8)',
                '&.Mui-checked': {
                  color: palette.secondary.light,
                },
              }}
            />
            <Typography>Enable</Typography>
          </div>
        </div>
        <div
          className="menu-option"
          style={{
            paddingLeft: '0.5rem',
            opacity: group.syncFilter ? 0.5 : 1,
            pointerEvents: group.syncFilter ? 'none' : 'auto',
          }}
        >
          <h3
            className="text-primary"
            style={{
              fontSize: fontSize.medium,
              margin: '10px 0 0 0',
            }}
          >
            And/Or
          </h3>

          <FormControl>
            <RadioGroup
              value={group.syncFilter ? 'all' : anyConditionMatch ? 'any' : 'all'}
              onChange={(e) => {
                const isAny = e.target.value === 'any';
                dispatch(updateDashboardAnyConditionMatch(isAny));
                if (dashboardTabTileList && dashboardTabTileList.length > 0) {
                  dashboardTabTileList.forEach((item: any) => {
                    dispatch(callQueryAPI(item.id, true));
                  });
                }
                dispatch(callQueryAPIDashboard(true));
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label="All conditions met"
                disabled={group.syncFilter}
              />
              <FormControlLabel
                value="any"
                control={<Radio size="small" />}
                label="Any condition met"
                disabled={group.syncFilter}
                sx={{marginTop: '-12px'}}
              />
            </RadioGroup>
          </FormControl>
        </div>

        <div
          className="menu-option"
          style={{
            paddingLeft: '0.5rem',
          }}
        >
          <h3
            className="text-primary"
            style={{
              margin: '0.3rem 0 0.3rem 0',
              fontSize: fontSize.medium,
            }}
          >
            Dashboard Tiles
          </h3>

          {dashboardTabTileList.map((item: any, index: number) => {
            return (
              <div
                className="option-content"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  transform: 'translateX(-4px)',
                }}
              >
                <Checkbox
                  checked={selectedGroupTabTilesList.includes(item.id)}
                  name={item.name}
                  disabled={item.disabled}
                  id={item.id}
                  sx={{
                    padding: 0,
                    transform: 'scale(0.8)',
                    '&.Mui-checked': {
                      color: palette.primary.main,
                    },
                  }}
                  onChange={(e) => handleCBChange(e)}
                />

                <Typography>
                  {item.name.length > 20 ? item.name.substring(0, 25) + '...' : item.name}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>
    </Popover>
  );
};

export default DashboardFilterGroupPopover;
